import React, { useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  generateAreaChartData,
  mapShortMonthsToFullMonths,
} from "../../data/data";
import { isBitramp } from "@/utils/utils";

interface CustomAreaChartProps {
  customData?: any;
  currency?: string;
}

const CustomAreaChart: React.FC<CustomAreaChartProps> = ({
  customData,
  currency,
}) => {
  const [activeLabel, setActiveLabel] = useState<string | null>(null);

  return (
    <ResponsiveContainer className="min-h-[29rem]" width="100%" height="100%">
      <AreaChart
        width={400}
        height={500}
        data={customData ? customData : generateAreaChartData}
        margin={{
          left: 50,
          bottom: 50,
          right: 50,
        }}
        onMouseMove={(e) => setActiveLabel(e.activeLabel ?? null)}
        onMouseLeave={() => setActiveLabel(null)}
      >
        <CartesianGrid
          horizontal={false}
          vertical={true}
          strokeDasharray="3 3"
        />
        <XAxis
          tickMargin={20}
          axisLine={false}
          tickLine={false}
          dataKey="name"
          tick={({ x, y, payload }) => (
            <text
              x={x}
              y={y}
              textAnchor="middle"
              fill={
                payload.value === activeLabel
                  ? isBitramp()
                    ? "#EE33D9"
                    : "#F00"
                  : "#D1D1D1"
              }
              fontWeight={payload.value === activeLabel ? "bold" : "normal"}
            >
              {payload.value}
            </text>
          )}
        />
        <YAxis
          tickMargin={20}
          axisLine={false}
          tickLine={false}
          tick={({ x, y, payload }) => (
            <text
              x={x}
              y={y}
              textAnchor="end"
              fill={
                payload.value === activeLabel
                  ? isBitramp()
                    ? "#EE33D9"
                    : "#F00"
                  : "#D1D1D1"
              }
              fontWeight={payload.value === activeLabel ? "bold" : "normal"}
            >
              {payload.value}
            </text>
          )}
        />

        <Tooltip
          cursor={{
            stroke: isBitramp() ? "#EE33D9" : "#FF0000",
            strokeWidth: 2,
            strokeDasharray: "3 3",
          }}
          content={({ active, payload }) => {
            if (active && payload) {
              return (
                <div className="bg-white p-2 rounded-lg shadow-md">
                  <p className="text-[#f00] font-[700]">
                    {mapShortMonthsToFullMonths(
                      payload?.[0]?.payload?.name ?? "N/A"
                    )}
                  </p>
                  <p className="text-black font-[400]">
                    {currency} {Number(payload[0].value ?? 0)}
                  </p>
                </div>
              );
            }
            return null;
          }}
        />
        <defs>
          <linearGradient
            id="colorUv"
            x1="369"
            y1="-182.629"
            x2="380.794"
            y2="582.029"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={`${isBitramp() ? "#EE33D9" : "#FF0000"}`} />
            <stop offset="0.572115" stopColor="white" />
          </linearGradient>
        </defs>

        <Area
          type="monotone"
          dataKey="uv"
          stroke={`${isBitramp() ? "#EE33D9" : "#FF0000"}`}
          fill="url(#colorUv)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default CustomAreaChart;
